import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, Search, MessageSquare, Check, CheckCheck, X,
  FileText, Image, Mic, Play, Pause, Download, Volume2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import VoiceRecorder from '../../components/chat/VoiceRecorder';
import FileUploader from '../../components/chat/FileUploader';

import { API_URL } from '../../config/api';

export default function ClientMessages() {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const audioRefs = useRef({});

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === convId);
      if (conv) {
        setSelectedConversation(conv);
      }
    }
  }, [searchParams, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!selectedConversation) return;
    
    const interval = setInterval(() => {
      fetchMessages(selectedConversation.id, true);
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedConversation]);

  const fetchConversations = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        
        if (data.length > 0 && !selectedConversation) {
          setSelectedConversation(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(
        `${API_URL}/api/client/conversations/${conversationId}/messages?limit=100`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      if (!silent) console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(
        `${API_URL}/api/client/conversations/${selectedConversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conversation_id: selectedConversation.id,
            content: newMessage.trim(),
            message_type: 'text'
          })
        }
      );

      if (response.ok) {
        setNewMessage('');
        fetchMessages(selectedConversation.id);
        fetchConversations();
        messageInputRef.current?.focus();
      } else {
        toast.error("Erreur lors de l'envoi du message");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendVoice = async (voiceData) => {
    if (!selectedConversation) return;
    
    const token = localStorage.getItem('client_token');
    
    try {
      // Create message with voice content
      const content = voiceData.transcription 
        ? `[Note vocale] ${voiceData.transcription}`
        : '[Note vocale]';
      
      const response = await fetch(
        `${API_URL}/api/client/conversations/${selectedConversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conversation_id: selectedConversation.id,
            content: content,
            message_type: 'voice',
            file_url: voiceData.file_url,
            file_name: voiceData.file_name
          })
        }
      );

      if (response.ok) {
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
    }
  };

  const handleSendFile = async (fileData) => {
    if (!selectedConversation) return;
    
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(
        `${API_URL}/api/client/conversations/${selectedConversation.id}/messages`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conversation_id: selectedConversation.id,
            content: `[Fichier] ${fileData.original_name}`,
            message_type: fileData.file_type,
            file_url: fileData.file_url,
            file_name: fileData.original_name
          })
        }
      );

      if (response.ok) {
        fetchMessages(selectedConversation.id);
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending file message:', error);
    }
  };

  const toggleAudioPlay = (messageId, audioUrl) => {
    if (playingAudio === messageId) {
      audioRefs.current[messageId]?.pause();
      setPlayingAudio(null);
    } else {
      // Pause any currently playing audio
      if (playingAudio && audioRefs.current[playingAudio]) {
        audioRefs.current[playingAudio].pause();
      }
      
      if (!audioRefs.current[messageId]) {
        audioRefs.current[messageId] = new Audio(`${API_URL}${audioUrl}`);
        audioRefs.current[messageId].onended = () => setPlayingAudio(null);
      }
      
      audioRefs.current[messageId].play();
      setPlayingAudio(messageId);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderMessageContent = (message) => {
    const isOwnMessage = message.sender_id === user.id;
    
    // Voice message
    if (message.message_type === 'voice' && message.file_url) {
      const isPlaying = playingAudio === message.id;
      const displayText = message.content.replace('[Note vocale] ', '').replace('[Note vocale]', '');
      
      return (
        <div className="space-y-2">
          <button
            onClick={() => toggleAudioPlay(message.id, message.file_url)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              isOwnMessage ? 'bg-white/20' : 'bg-slate-100'
            }`}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5" />
            )}
            <Volume2 className="w-4 h-4" />
            <span className="text-sm">Note vocale</span>
          </button>
          {displayText && (
            <p className="text-sm italic opacity-80">"{displayText}"</p>
          )}
        </div>
      );
    }
    
    // File/Image message
    if ((message.message_type === 'file' || message.message_type === 'image') && message.file_url) {
      const isImage = message.message_type === 'image' || 
        message.file_name?.match(/\.(jpg|jpeg|png|gif)$/i);
      
      return (
        <div className="space-y-2">
          {isImage ? (
            <a 
              href={`${API_URL}${message.file_url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block"
            >
              <img 
                src={`${API_URL}${message.file_url}`} 
                alt={message.file_name}
                className="max-w-[200px] rounded-lg"
              />
            </a>
          ) : (
            <a
              href={`${API_URL}${message.file_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                isOwnMessage ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-100 hover:bg-slate-200'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="text-sm truncate max-w-[150px]">{message.file_name}</span>
              <Download className="w-4 h-4" />
            </a>
          )}
        </div>
      );
    }
    
    // Regular text message
    return <p className="whitespace-pre-wrap break-words">{message.content}</p>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="messages-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 h-[calc(100vh-12rem)] flex overflow-hidden" data-testid="messages-container">
      {/* Conversations List */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-slate-100 flex flex-col ${
        selectedConversation ? 'hidden md:flex' : 'flex'
      }`}>
        {/* Search */}
        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une conversation..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
              data-testid="search-conversations"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-rm-text-secondary">Aucune conversation</p>
              <p className="text-xs text-slate-400 mt-1">Vos conversations avec ton équipe apparaîtront ici</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                data-testid={`conversation-${conv.id}`}
                className={`w-full p-4 flex items-center gap-3 border-b border-slate-50 hover:bg-slate-50 transition-colors text-left ${
                  selectedConversation?.id === conv.id ? 'bg-rm-primary/5' : ''
                }`}
              >
                <div className="w-12 h-12 bg-rm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-rm-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-rm-text truncate">{conv.title}</p>
                    {conv.last_message_at && (
                      <span className="text-xs text-rm-text-secondary">
                        {formatTime(conv.last_message_at)}
                      </span>
                    )}
                  </div>
                  {conv.last_message && (
                    <p className="text-sm text-rm-text-secondary truncate">{conv.last_message}</p>
                  )}
                </div>
                {conv.unread_count > 0 && (
                  <span className="bg-rm-secondary text-white text-xs px-2 py-1 rounded-full">
                    {conv.unread_count}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${
        selectedConversation ? 'flex' : 'hidden md:flex'
      }`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-100 flex items-center gap-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
                data-testid="back-to-list"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-rm-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-rm-text">{selectedConversation.title}</p>
                <p className="text-xs text-green-500">En ligne</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-testid="messages-list">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-rm-text-secondary">Aucun message</p>
                  <p className="text-sm text-slate-400">Envoyez le premier message !</p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isOwnMessage = message.sender_id === user.id;
                  const showDate = index === 0 || 
                    formatDate(messages[index - 1].created_at) !== formatDate(message.created_at);

                  return (
                    <div key={message.id}>
                      {showDate && (
                        <div className="text-center my-4">
                          <span className="text-xs bg-white px-3 py-1 rounded-full text-rm-text-secondary shadow-sm">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      )}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        data-testid={`message-${message.id}`}
                      >
                        <div className={`max-w-[75%] ${isOwnMessage ? 'order-2' : ''}`}>
                          {!isOwnMessage && (
                            <p className="text-xs text-rm-text-secondary mb-1 ml-3">
                              {message.sender_name}
                            </p>
                          )}
                          <div
                            className={`px-4 py-2 rounded-2xl ${
                              isOwnMessage
                                ? 'bg-rm-primary text-white rounded-br-md'
                                : 'bg-white text-rm-text rounded-bl-md shadow-sm'
                            }`}
                          >
                            {renderMessageContent(message)}
                            <div className={`flex items-center justify-end gap-1 mt-1 ${
                              isOwnMessage ? 'text-white/70' : 'text-slate-400'
                            }`}>
                              <span className="text-xs">{formatTime(message.created_at)}</span>
                              {isOwnMessage && (
                                message.is_read ? 
                                  <CheckCheck className="w-3 h-3" /> : 
                                  <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 bg-white relative">
              <div className="flex items-center gap-2">
                <FileUploader 
                  onFileUploaded={handleSendFile}
                  disabled={sendingMessage}
                />
                <VoiceRecorder 
                  onSendVoice={handleSendVoice}
                  disabled={sendingMessage}
                />
                <input
                  ref={messageInputRef}
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Écris ton message..."
                  className="flex-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sendingMessage}
                  className="bg-rm-primary hover:bg-rm-primary/90 rounded-full p-2 h-10 w-10"
                  data-testid="send-message-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-slate-50">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-rm-text-secondary">Sélectionnez une conversation</p>
              <p className="text-xs text-slate-400 mt-1">ou attendez qu'un assistant te contacte</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
